import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useQuotes, useDeleteQuote, useConvertToInvoice, useUpdateQuoteStatus,
  QUOTE_STATUSES, type QuoteStatus,
} from '../hooks/useQuotes';
import { useNextInvoiceNumber } from '@/features/invoices/hooks/useInvoices';
import { FileSignature, Plus, Trash2, ArrowRightCircle, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

export default function Quotes() {
  const navigate = useNavigate();
  const { data: quotes = [], isLoading } = useQuotes();
  const deleteQuote = useDeleteQuote();
  const convertToInvoice = useConvertToInvoice();
  const nextInvoiceNumber = useNextInvoiceNumber();
  const updateStatus = useUpdateQuoteStatus();
  const [filter, setFilter] = useState<QuoteStatus | 'all'>('all');

  const filtered = filter === 'all' ? quotes : quotes.filter((q) => q.status === filter);

  const handleDelete = (id: string) => {
    deleteQuote.mutate(id, {
      onSuccess: () => toast.success('Devis supprimé'),
      onError: (err: any) => toast.error(`Erreur : ${err?.message}`),
    });
  };

  const handleConvert = async (quote: (typeof quotes)[0]) => {
    try {
      const invoiceNumber = await nextInvoiceNumber.mutateAsync();
      await convertToInvoice.mutateAsync({ quote, invoiceNumber });
      toast.success(`Devis converti → ${invoiceNumber}`);
      navigate('/invoices');
    } catch (err: any) {
      toast.error(`Erreur conversion : ${err?.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileSignature className="text-primary" size={24} />
          <h1 className="text-2xl font-bold font-display">Devis</h1>
        </div>
        <Button onClick={() => navigate('/quotes/new')} className="gap-2">
          <Plus size={16} /> Nouveau devis
        </Button>
      </div>

      {/* Filtres statut */}
      <div className="flex gap-2 flex-wrap">
        {[
          { value: 'all' as const, label: 'Tous' },
          ...QUOTE_STATUSES.map((s) => ({ value: s.value, label: s.label.fr })),
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filter === f.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <FileSignature className="mx-auto text-muted-foreground mb-3" size={32} />
          <p className="text-muted-foreground">
            Aucun devis{filter !== 'all' ? ' avec ce statut' : ''}.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((q) => (
            <li key={q.id} className="glass-card p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm">{q.invoice_number}</p>
                    <Select
                      value={q.status}
                      onValueChange={(v) =>
                        updateStatus.mutate({ id: q.id, status: v as QuoteStatus })
                      }
                    >
                      <SelectTrigger className="h-7 w-28 text-xs px-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {QUOTE_STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value} className="text-xs">
                            {s.label.fr}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-sm text-muted-foreground truncate mt-0.5">
                    {q.client_name || 'Client inconnu'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {q.due_date
                      ? `Valide jusqu'au ${new Date(q.due_date).toLocaleDateString('fr-FR')}`
                      : `Créé le ${new Date(q.created_at).toLocaleDateString('fr-FR')}`}
                  </p>
                </div>

                <div className="text-right shrink-0">
                  <p className="font-bold text-base tabular-nums">
                    {(q.total_ttc ?? 0).toFixed(2)} €
                  </p>
                  {q.deposit_amount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Acompte {q.deposit_amount.toFixed(2)} €
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1.5">
                  {q.status === 'accepted' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => handleConvert(q)}
                      disabled={convertToInvoice.isPending || nextInvoiceNumber.isPending}
                    >
                      <ArrowRightCircle size={14} />
                      Convertir
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/quotes/new?edit=${q.id}`)}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(q.id)}
                    disabled={deleteQuote.isPending}
                  >
                    <Trash2 size={14} className="text-destructive" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
